import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'shared/utils/toast';
import { AddDocumentDialog } from './AddDocumentDialog';
import { fmDocumentService } from '../api/FMDocumentService';
import { DocumentColumns } from '../documentsLM';
import { EnhancedCard } from 'components/enhanced';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';

const MAX_NUMBER_TAGS = 999;

interface IDocumentTableCardComponentParams {
  originalService: string; // The this string will be APPENDED at the URL
  objectId: number; // The object ID to be used while linking the document to the object
  dictParams: any;
  folder: string;
  folder_description: string;
  isFornitore?: boolean;
  disableEdit?: boolean;
}

export const DocumentTableCardComponent = (params: IDocumentTableCardComponentParams) => {
  const {
    originalService,
    dictParams,
    objectId,
    folder,
    folder_description,
    isFornitore,
    disableEdit
  } = params;
  const { t } = useTranslation();

  const refresh = useRef(null);
  /**
   * Function to handle the download of a document.
   * If the document ID is provided, download the attachment.
   *
   * @param {Object} document - The document object with the ID.
   */
  const handleDownloadDocument = useCallback((document: any) => {
    if (document?.id) {
      fmDocumentService.downloadLmAttachment(originalService, document.id);
    }
  }, []);
  /**
   * Function to handle the deletion of a document.
   * If the document ID is provided, delete the document.
   *
   * @param {Object} document - The document object with the ID.
   */
  const handleDeleteDocument = (documentId) => {
    if (documentId) {
      fmDocumentService.delete(documentId).then(() => {
        toast.success(t('entities.document.delete.success'));
        refresh.current?.();
      });
    }
  };

  /**
   * Columns for the document grid.
   * Includes a download and delete button for each document.
   */
  const documentColumns: GridColDef[] = useMemo(() => {
    return DocumentColumns(handleDownloadDocument, handleDeleteDocument);
  }, [handleDownloadDocument, handleDeleteDocument]);

  const onSubmit = useCallback(
    (values: any, suppliers: any[]) => {
      const selectedSupplier = values.supplier
        ? suppliers.find((supplier) => supplier.id === values.supplier)
        : undefined;

      const compositeFolderDescription =
        folder_description +
        (selectedSupplier ? ` Fornitore: ${selectedSupplier.label}` : '') +
        (values.tipology && typeof values.tipology === 'object' && values.tipology.name
          ? ` Tipologia: ${values.tipology.name}`
          : '') +
        (values.category && values.category.name ? ` Categoria: ${values.category.name}` : '');

      fmDocumentService
        .uploadDocument(
          values.tipology,
          values.attachment.item(0),
          values.name,
          values.description || '',
          folder,
          compositeFolderDescription,
          values.category || '',
          values.supplier,
          values.warehouse,
          originalService,
          [objectId]
        )
        .then((res) => {
          if (res) {
            toast.success(t('entities.document.upload-success'));
          } else {
            toast.error(t('entities.document.upload-error'));
          }
          refresh.current?.();
        });
    },
    [folder, folder_description, originalService, objectId, t]
  );

  const getDocuments = async (
    limit: number,
    offset: number,
    params: Record<string, string> = {}
  ) => {
    const mergedParams = {
      ...dictParams
      // ...params // i documenti sono già filtrati via dictParams, l'aggiunta dei Warehouse "nasconderebbe" troppo
    };
    const result = await fmDocumentService.getAllPaginatedDocuments(
      originalService,
      limit,
      offset,
      mergedParams
    );
    return result;
  };

  return (
    <EnhancedCard
      title={t('global.documents-storage')}
      subtitle="Gestisci e visualizza i documenti associati."
      buttons={
        !disableEdit && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddDocumentDialog
              onSubmit={onSubmit}
              originalService={originalService}
              objectId={objectId}
              isFornitore={isFornitore}
            />
          </Box>
        )
      }>
      {/* GRIGLIA DEI DOCUMENTI AVANZATA */}
      <Box
        sx={{
          width: '100%',
          '& .MuiDataGrid-root': {
            borderRadius: 2, // Arrotonda gli angoli della tabella se usa MUI DataGrid internamente
            borderColor: 'divider'
          }
        }}>
        <EnhancedDataGrid
          columns={documentColumns}
          service={fmDocumentService}
          getMethod={getDocuments}
          outerParams={dictParams}
          refresh={refresh}
          disableEdit={disableEdit}
        />
      </Box>
    </EnhancedCard>
  );
};
